// src/home/Chatbot/chat.service.js
import { axiosInstance } from "common/constants";
import i18n from "i18n/config";

export const chatService = {
  sendQuery,
  getStatus,
  clearConversation,
};

const CHAT_PATH = process.env.REACT_APP_CHAT_PATH || "/i/v1/chatbot/chat";
const CHAT_STATUS_PATH = process.env.REACT_APP_CHAT_STATUS_PATH || "/i/v1/chatbot/status";
const USE_GET = process.env.REACT_APP_CHAT_METHOD === "GET";
const TENANT_ID = process.env.REACT_APP_CHAT_TENANT_ID || "default";
const CHAT_SCOPE = process.env.REACT_APP_CHAT_SCOPE || "default";
const MAX_TURNS_TO_SEND = Number(process.env.REACT_APP_CHAT_MAX_TURNS_TO_SEND) || 8;
const MAX_CHARS_PER_MESSAGE = Number(process.env.REACT_APP_CHAT_MAX_CHARS_PER_MESSAGE) || 1000;
const SESSION_KEY_PREFIX = "chatbot:conversation";
const inMemoryConversations = new Map();

function getErrorMessage(status, data) {
  const serverMsg = data && (data.detail || data.message || data.error);
  if (serverMsg) return typeof serverMsg === "string" ? serverMsg : JSON.stringify(serverMsg);
  if (status === 400 && data && Object.keys(data).length > 0) {
    return i18n.t('chatbot.errors.badRequestWithDetails', {
      details: JSON.stringify(data),
      defaultValue: 'Bad request (400): {{details}}'
    });
  }
  switch (status) {
    case 400:
      return i18n.t('chatbot.errors.badRequest', {
        defaultValue: 'Bad request (400). Request must include tenant_id and question. Check backend response for details.'
      });
    case 404:
      return i18n.t('chatbot.errors.endpointNotFound', {
        defaultValue: 'Chat endpoint not found (404). Set REACT_APP_BASE_URL and REACT_APP_CHAT_PATH in .env to match your backend bridge route (for example: REACT_APP_CHAT_PATH=/i/v1/chatbot/chat).'
      });
    case 500:
      return i18n.t('chatbot.errors.serverError', {
        defaultValue: 'Server error (500). Check your backend logs for the cause.'
      });
    default:
      return i18n.t('chatbot.errors.httpStatus', {
        status: status || 'unknown',
        defaultValue: 'HTTP {{status}}'
      });
  }
}

function normalizeApiPath(pathname) {
  const path = String(pathname || "");
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return path.startsWith("/") ? path.slice(1) : path;
}

function buildGetParams(text, conversation) {
  const params = { tenant_id: TENANT_ID, question: text };
  if (conversation.length > 0) {
    params.conversation = JSON.stringify(conversation);
  }
  return params;
}

function normalizeChatResponse(data) {
  if (!data || typeof data !== "object") return data;
  if (!data.image_url) return data;
  const url = String(data.image_url);
  if (url.startsWith("http://") || url.startsWith("https://")) return data;
  const base = (axiosInstance.defaults.baseURL || "").replace(/\/$/, "");
  const path = url.startsWith("/") ? url : `/${url}`;
  return { ...data, image_url: `${base}${path}` };
}

function throwAxiosError(error) {
  const status = error && error.response ? error.response.status : undefined;
  const data = error && error.response ? (error.response.data || {}) : {};
  const msg = getErrorMessage(status, data);
  throw Object.assign(new Error(msg), { status, data });
}

function trimToMaxChars(content) {
  return String(content || "").slice(0, MAX_CHARS_PER_MESSAGE).trim();
}

function isValidRole(role) {
  return role === "user" || role === "assistant";
}

function normalizeTurn(turn) {
  if (!turn || typeof turn !== "object") return null;
  if (!isValidRole(turn.role)) return null;

  const content = trimToMaxChars(turn.content);
  if (!content) return null;

  return {
    role: turn.role,
    content,
    ...(turn.timestamp ? { timestamp: turn.timestamp } : {}),
  };
}

function boundTurns(turns) {
  return turns.slice(-MAX_TURNS_TO_SEND);
}

function getConversationSessionKey(tenantId = TENANT_ID, chatScope = CHAT_SCOPE) {
  return `${SESSION_KEY_PREFIX}:${tenantId}:${chatScope}`;
}

function loadConversation(sessionKey = getConversationSessionKey()) {
  const turns = inMemoryConversations.get(sessionKey);
  return Array.isArray(turns) ? turns : [];
}

function saveConversation(sessionKey = getConversationSessionKey(), turns = []) {
  const sanitizedTurns = boundTurns((Array.isArray(turns) ? turns : []).map(normalizeTurn).filter(Boolean));
  inMemoryConversations.set(sessionKey, sanitizedTurns);
  return sanitizedTurns;
}

function clearConversation(sessionKey = getConversationSessionKey()) {
  inMemoryConversations.delete(sessionKey);
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    // Also clear any legacy persisted conversation from older builds.
    window.sessionStorage.removeItem(sessionKey);
  } catch (error) {
    // Ignore storage failures and keep the app functional.
  }
}

function saveAssistantTurn(sessionKey, nextConversation, data) {
  const assistantTurn = normalizeTurn({
    role: "assistant",
    content: data && (data.answer || data.response || ""),
  });
  if (assistantTurn) {
    saveConversation(sessionKey, [...nextConversation, assistantTurn]);
  }
}

function getStatus() {
  const url = normalizeApiPath(CHAT_STATUS_PATH);
  return axiosInstance
    .get(url)
    .then((res) => res.data)
    .catch((error) => throwAxiosError(error));
}

function sendQuery(text, options = {}) {
  const url = normalizeApiPath(CHAT_PATH);
  const sessionKey = getConversationSessionKey(TENANT_ID, options.chatScope || CHAT_SCOPE);
  const currentConversation = loadConversation(sessionKey);
  const userTurn = normalizeTurn({ role: "user", content: text });
  const nextConversation = boundTurns([...currentConversation, ...(userTurn ? [userTurn] : [])]);
  saveConversation(sessionKey, nextConversation);

  if (USE_GET) {
    const params = buildGetParams(text, nextConversation);
    return axiosInstance
      .get(url, { params })
      .then((res) => normalizeChatResponse(res.data))
      .then((data) => {
        saveAssistantTurn(sessionKey, nextConversation, data);
        return data;
      })
      .catch((error) => throwAxiosError(error));
  }

  const body = { tenant_id: TENANT_ID, question: text };
  if (nextConversation.length > 0) {
    body.conversation = nextConversation;
  }
  return axiosInstance
    .post(url, body)
    .then((res) => normalizeChatResponse(res.data))
    .catch((error) => {
      const status = error && error.response ? error.response.status : undefined;
      if (status !== 405) {
        throwAxiosError(error);
      }
      const params = buildGetParams(text, nextConversation);
      return axiosInstance
        .get(url, { params })
        .then((res) => normalizeChatResponse(res.data))
        .catch((fallbackError) => throwAxiosError(fallbackError));
    })
    .then((data) => {
      saveAssistantTurn(sessionKey, nextConversation, data);
      return data;
    });
}
